import * as dateFns from 'date-fns';
import { DateUtils, Currency } from '../../utils';
import { SessionProvider } from 'core/infrastructure';

import { Res, PosGroupInListDTO, SerialNumberDTO, TicketLineDTO } from 'core/api';

function padWithZeros(value: number, size: number): string {
	let padded = '' + value;
	while (padded.length < size)
		padded = '0' + padded;
	return padded;
}

const LOCALE = (function () {
	if (!navigator.language)
		return 'es-ES';

	return navigator.language;
})();

export const asBool = (value: any) => value ? Res.Msg.MSG_YES() : Res.Msg.MSG_NO();

// Mantener fijos estos objetos acelera todo porque toLocaleString puede
// usar (en Chrome) el NumberFormat que tiene en caché. Se nota mucho
// al exportar a excels grandes. En los navegadores viejos sin api de
// Intl, hago el toLocaleString, que es lo único que puedo hacer en cualquier
// caso.
const toLocaleString = (x: any) => x.toLocaleString();
const NumberFormats = {
	PriceNoGrouping: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: false,
		minimumFractionDigits: Currency.DECIMAL_PLACES,
	}).format : toLocaleString,
	Price: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: true,
		minimumFractionDigits: Currency.DECIMAL_PLACES,
		maximumFractionDigits: Currency.DECIMAL_PLACES
	}).format : toLocaleString,
	PurchasePrice: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: true,
		minimumFractionDigits: 2,
		maximumFractionDigits: 5
	}).format : toLocaleString,
	PurchasePriceNoGrouping: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: false,
		minimumFractionDigits: 2,
		maximumFractionDigits: 5
	}).format : toLocaleString,
	QuantityNoGrouping: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: false,
		minimumFractionDigits: 3,
	}).format : toLocaleString,
	Quantity: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: true,
		minimumFractionDigits: 3,
	}).format : toLocaleString,
	ShortQuantity: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: true,
		minimumFractionDigits: 0,
		maximumFractionDigits: 3
	}).format : toLocaleString,
	Number: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: true,
		minimumFractionDigits: 0,
	}).format : toLocaleString,
	NumberNoGrouping: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		useGrouping: false,
		minimumFractionDigits: 0,
	}).format : toLocaleString,
	Money: typeof Intl !== 'undefined' && Intl.NumberFormat ? new Intl.NumberFormat(LOCALE, {
		style: 'currency',
		currency: Currency.ISOCODE
	}).format : toLocaleString,
	MoneyWithoutDecimals: typeof Intl !== 'undefined' && Intl.NumberFormat ? Intl.NumberFormat(LOCALE, {
		style: 'currency',
		currency: Currency.ISOCODE,
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	}).format : toLocaleString
};

export const asPriceNoGrouping = (value: any) => value == null ? '' : NumberFormats.PriceNoGrouping(value);

export const asNumber = (value: any) => value == null ? '' : NumberFormats.Number(value);

export const asNumberNoGrouping = (value: any) => value == null ? '' : NumberFormats.NumberNoGrouping(value);

export const asPrice = (value: any) => value == null ? '' : NumberFormats.Price(value);

export const asDate = (value: any) => value == null ? '' : DateUtils.formatDate(value, 'dd/MM/yyyy');

// Aunque parece que se podría usar una única función para asPercentage y asTaxPercentage con un segundo parámetro
// opcional para el número de decimales, eso da lugar a problemas de bivarianza cuando se intenta usar esa función
// en un DataGridColumn porque acaba recibiendo como segundo parámetro un objeto del tipo de los items del DataGrid
// y se genera un error.
export const asPercentage = (value: any) => value == null ? '' : `${asDecimalWith(2)(value)} %`;
export const asTaxPercentage = (value: any) => value == null ? '' : `${asDecimalWith(3)(value)} %`;

export const asHumanReadableDate = (value: any) => {

	if (value == null)
		return '';

	const differenceInDays = DateUtils.getDifferenceInDays(value, new Date());

	if (differenceInDays === -1)
		return Res.Msg.MSG_YESTERDAY();

	if (differenceInDays === 0)
		return Res.Msg.MSG_TODAY();

	return DateUtils.formatDate(value, 'dd MMM');
};

export const asTime = (value: any) => value == null ? '' : DateUtils.formatDate(value, 'HH:mm');

export const asMinutes = (value: number) => {
	const hours = Math.floor(value / 60);
	const minutes = value % 60;

	const formattedHours = hours.toString().padStart(2, '0');
	const formattedMinutes = minutes.toString().padStart(2, '0');

	return `${formattedHours}:${formattedMinutes}`;
};

export const asDateAndTime = (value: any) => value == null ? '' : DateUtils.formatDate(value, 'dd/MM/yyyy HH:mm');

export const asQuantity = (value: any) => value == null ? '' : NumberFormats.Quantity(value);

export const asQuantityNoGrouping = (value: any) => value == null ? '' : NumberFormats.QuantityNoGrouping(value);

export const asShortQuantity = (value: any) => value == null ? '' : NumberFormats.ShortQuantity(value);

export const asPosGroup = (value: PosGroupInListDTO) => {
	if (SessionProvider.isHub())
		return `${value.Workplace} / ${value.Name}`;
	return value.Name;
};

// TODO: Esto podría ser interesante cachearlo como se hace con los number format si
//       empieza a ser un cuello de botella al generar excels
export const asDecimalWith = (places: number, useGrouping = true) => (value: any) => value == null ? '' : value.toLocaleString(LOCALE, {
	useGrouping: useGrouping,
	minimumFractionDigits: places,
	maximumFractionDigits: places
});

export const asInt = (value: any) => asDecimalWith(0, true)(value);
export const asPurchasePrice = (value: any) => value == null ? '' : NumberFormats.PurchasePrice(value);
export const asPurchasePriceNoGrouping = (value: any) => value == null ? '' : NumberFormats.PurchasePriceNoGrouping(value);

export const asNumberWithDigits = (digits: number) => (value: any) => value == null ? '' : value.toLocaleString(LOCALE, {
	useGrouping: false,
	minimumIntegerDigits: digits
});

export const asMoney = (value: any): string => {
	return value == null ? '' : NumberFormats.Money(value);
};

export const asMoneyWithoutDecimals = (value: any) => value == null ? '' : NumberFormats.MoneyWithoutDecimals(value);

// OJO: Este formato debería ser ordenable lexicográficamente porque se usa en los grids
export const asDocumentNumber = (serie: string, value: number) => `${serie}-${padWithZeros(value, 6)}`;

export const asFullPurchaseDocumentNumber = (document: {
	SerialNumber: SerialNumberDTO;
	SupplierDocumentNumber: string
}) => {
	const serial = document.SerialNumber.Number > 0
		? asDocumentNumber(document.SerialNumber.SerieName, document.SerialNumber.Number)
		: Res.Msg.MSG_NEW();
	const supplier = document.SupplierDocumentNumber ? ` - ${document.SupplierDocumentNumber}` : '';
	return `${serial}${supplier}`;
};

export function asProductName(product: { Name: string, Size: { Name: string }, Color: { Name: string } }): string {
	const sizeAndColorName = product.Size.Name ? ` ${product.Size.Name}/${product.Color.Name}` : '';
	return `${product.Name.trim()}${sizeAndColorName}`;
}

export const asTicketBAIDataSubmissionStatus = (status: number) => {
	// No puedo tipar bien la función (de momento) porque el TicketBAIDataSubmissionStatus no está en ningún
	// DTO, y sólo se usa en una consulta personaliza.
	switch (status) {
		case 0: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_PENDING();
		case 1: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_DISCARDED();
		case 2: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_TRANSIENT_ERROR();
		case 3: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_REJECTED();
		case 4: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_ACCEPTED();
		case 5: return Res.Const.ENUM_TICKET_BAI_DATA_SUBMISSION_STATUS_ACCEPTED();
	}

	throw Error('Cannot convert ${status} to TicketBAIDataSubmissionStatus');
};

export const asMinutesAsHHmm = (minutesValue: any, showSuffix?: 'INCLUDE_HOUR_SUFFIX') => {

	if (minutesValue === null || minutesValue === undefined)
		return '';

	const totalMinutesRounded = Math.round(minutesValue);
	const hours = Math.floor(totalMinutesRounded / 60);
	const minutes = Math.floor(totalMinutesRounded % 60);

	const formattedHours = hours.toString().padStart(2, '0');
	const formattedMinutes = minutes.toString().padStart(2, '0');

	return `${formattedHours}:${formattedMinutes}` + (showSuffix === 'INCLUDE_HOUR_SUFFIX' ? ' h' : '');
};

export const asMinutesAsHHmmss = (minutesValue: any, showSuffix?: 'INCLUDE_HOUR_SUFFIX') => {

	if (minutesValue === null || minutesValue === undefined) return '';

	const totalSeconds = Math.round(minutesValue * 60);
	const hours = Math.floor(totalSeconds / 3600);
	const minutes = Math.floor((totalSeconds % 3600) / 60);
	const seconds = totalSeconds % 60;

	const formattedHours = hours.toString().padStart(2, '0');
	const formattedMinutes = minutes.toString().padStart(2, '0');
	const formattedSeconds = seconds.toString().padStart(2, '0');

	return `${formattedHours}:${formattedMinutes}:${formattedSeconds}` + (showSuffix === 'INCLUDE_HOUR_SUFFIX' ? ' h' : '');
};

export const asLowHigh = (value: any) => {

	if (value === null)
		return '';

	const intValue = parseInt(value);
	return intValue > 0 ? Res.Msg.MSG_HIGH().toUpperCase() : Res.Msg.MSG_LOW().toUpperCase();
};

export const asMenuEngineeringScore = (value: any) => {

	if (value === null)
		return '';

	const intValue = parseInt(value);

	switch (intValue) {
		case 0:
			return Res.Doc.SQL_MENU_ENGINEERING_SCORE_STAR();
		case 1:
			return Res.Doc.SQL_MENU_ENGINEERING_SCORE_WORKHORSE();
		case 2:
			return Res.Doc.SQL_MENU_ENGINEERING_SCORE_PUZZLE();
		case 3:
			return Res.Doc.SQL_MENU_ENGINEERING_SCORE_DOG();
		default:
			return '';
	}
};

export const asLocationDescription = function ({ FriendlyName, Location: { SaleCenterName, SaleLocationName } }: { FriendlyName: string, Location: { SaleCenterName: string, SaleLocationName: string } }) {
	if (!FriendlyName)
		return SaleCenterName + ' ' + SaleLocationName;
	else if (FriendlyName.match(/^\d+$/))
		return SaleCenterName + ' ' + SaleLocationName + '/' + FriendlyName;

	return SaleCenterName + ' ' + FriendlyName;
};

export const asDefaultLocationDescription = function ({ SaleCenterFullName, SaleCenterName, SaleLocationName }: { SaleCenterName: string, SaleLocationName: string, SaleCenterFullName: string }) {
	if (!SaleCenterFullName)
		return SaleCenterName + ' ' + SaleLocationName;
	return SaleCenterFullName + ' ' + SaleLocationName;
};

export const asSoldProductDescription = function (line: TicketLineDTO) {
	let description = line.Product.SaleFormatName;
	if (line.Product.Size.Id !== 0 && line.Product.Color.Id !== 0)
		description += ` ${line.Product.Size.Name}/${line.Product.Color.Name}`;
	if (line.Addins.length) {
		// OJO: Usar el ProductName en lugar del SaleFormatName es intencionado para no acabar poniendo JB c/ Lata Coca Cola.
		description += Res.Web.LAST_LINE_CONTROL_WITH_ADDIN_LBL(line.Addins.map(x => x.ProductName).join(', '));
	}
	return description;
};

export function asSaleFormatDescription(data: { SaleFormatName: string, SizeName: string, ColorName: string } | undefined) {
	if (data === undefined)
		return '';

	const sizeAndColorName = data.SizeName ? ` ${data.SizeName}/${data.ColorName}` : '';
	return `${data.SaleFormatName.trim()}${sizeAndColorName}`;
}

export function asFiscalNameWithCif(customer: { FiscalName: string, Cif: string } | undefined) {
	if (customer === undefined)
		return '';

	return Res.Msg.MSG_FISCAL_NAME_AND_CIF(customer.FiscalName.trim(), customer.Cif.trim());
}

export const asDeliveryDate = (date: Date, now: Date = new Date()): string => {

	if (isCloseTime(date, now))
		return DateUtils.formatDate(date, 'HH:mm');

	if (dateFns.isSameDay(date, now))
		return Res.Web.SHOW_TAKE_OUT_ORDERS_TODAY();

	const yesterday = dateFns.addDays(now, -1);

	if (dateFns.isSameDay(date, yesterday))
		return Res.Web.SHOW_TAKE_OUT_ORDERS_YESTERDAY();

	const tomorrow = dateFns.addDays(now, 1);

	if (dateFns.isSameDay(date, tomorrow))
		return Res.Web.SHOW_TAKE_OUT_ORDERS_TOMORROW();

	return DateUtils.formatDate(date, 'dd/MM');
};

export const numberAsMonth = (month: number): string => {
	const names = [
		Res.Msg.MSG_MONTH_JANUARY(),
		Res.Msg.MSG_MONTH_FEBRUARY(),
		Res.Msg.MSG_MONTH_MARCH(),
		Res.Msg.MSG_MONTH_APRIL(),
		Res.Msg.MSG_MONTH_MAY(),
		Res.Msg.MSG_MONTH_JUNE(),
		Res.Msg.MSG_MONTH_JULY(),
		Res.Msg.MSG_MONTH_AUGUST(),
		Res.Msg.MSG_MONTH_SEPTEMBER(),
		Res.Msg.MSG_MONTH_OCTOBER(),
		Res.Msg.MSG_MONTH_NOVEMBER(),
		Res.Msg.MSG_MONTH_DECEMBER(),
	] as const;
	return names[month - 1];
};

export const numberAsDayOfWeek = (dayOfWeek: number): string => {
	const names = [
		Res.Msg.MSG_DAY_MONDAY(),
		Res.Msg.MSG_DAY_TUESDAY(),
		Res.Msg.MSG_DAY_WEDNESDAY(),
		Res.Msg.MSG_DAY_THURSDAY(),
		Res.Msg.MSG_DAY_FRIDAY(),
		Res.Msg.MSG_DAY_SATURDAY(),
		Res.Msg.MSG_DAY_SUNDAY(),
	] as const;
	return names[dayOfWeek - 1];
};

export const isCloseTime = (dateTime: Date, now: Date = new Date()): boolean => {
	return (dateTime > DateUtils.addHours(now, -12) && dateTime < DateUtils.addHours(now, 12));
};